<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-form ref="form">
          <v-col cols="12">
            <h2 class="primary--text font-weight-bold pt-5 pl-2">
              {{ task && task.id ? $t('buttons:edit-specific', {specific: $t('common:task')}) : $t('buttons:add-specific', {specific: $t('common:task')}) }}
            </h2>
            <v-card elevation="0" flat tile>
              <v-card-text>
                <v-row justify="space-around">
                  <v-col cols="12" md="3">
                    <h4 class="primary--text font-weight-medium">
                      {{ $t('book/issue:add-issue-form.facility-out-of-service') }}
                    </h4>
                    <v-radio-group
                        :readonly="readOnly"
                        row
                        v-model="task.inService">
                      <v-radio
                          :label="$t('buttons:yes')"
                          :value="true"
                      ></v-radio>
                      <v-radio
                          :label="$t('buttons:no')"
                          :value="false"
                      ></v-radio>
                    </v-radio-group>
                    <v-checkbox
                        :label="$t('book/issue:common.vandalism')"
                        v-model="task.vandalism"
                        :readonly="readOnly"/>
                  </v-col>
                  <v-col cols="12" md="3">
                    <h3 class="primary--text font-weight-medium">
                      {{ $t('book/issue:add-issue-form.short-description') }}
                    </h3>
                    <v-text-field
                        :label="$t('book/issue:add-issue-form.short-problem-description')"
                        v-model="task.name"
                        :readonly="readOnly"
                        clearable
                        outlined
                        :rules="[ruleRequired($t('utils:rules.required'))]"
                    />
                    <v-textarea
                        v-model="task.description"
                        clearable
                        outlined
                        :readonly="readOnly"
                        :label="$t('book/issue:add-issue-form.problem-description')"
                        :rules="[ruleRequired($t('utils:rules.required'))]"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <h4 class="primary--text font-weight-medium">
                      {{ $t('book/issue:add-issue-form.contact-info') }}
                    </h4>
                    <v-autocomplete
                        v-if="isAdmin"
                        :items="customers"
                        v-model="currentCompany"
                        @click:clear="clearCompany"
                        clearable
                        outlined
                        item-text="translatedName"
                        item-value="id"
                        :disabled="isEdit || facilityUnchangeable"
                        :label="$t('common:customer')"
                    />
                    <v-autocomplete
                        :items="facilities"
                        v-model="selectedFacility"
                        clearable
                        outlined
                        :readonly="readOnly"
                        :disabled="isEdit || !facilities || facilities.length === 0 || facilityUnchangeable"
                        item-text="name"
                        item-value="id"
                        :label="$t('book/issue:add-issue-form.facility-number')"
                        :rules="[ruleRequired($t('utils:rules.required'))]"
                    />
                    <v-autocomplete
                        :items="userList"
                        v-model="task.contact"
                        :disabled="!userList || userList.length === 0 || isEdit"
                        clearable
                        outlined
                        :readonly="readOnly"
                        item-text="name"
                        item-value="id"
                        :label="$t('book/issue:add-issue-form.contact')"
                        :rules="[ruleRequired($t('utils:rules.required'))]"
                    />
                    <v-text-field
                        v-model="task.externalContact"
                        clearable
                        outlined
                        :readonly="readOnly"
                        :label="$t('book/issue:add-issue-form.contact-name')"
                    />
                    <v-text-field
                        v-model="task.externalPhoneContact"
                        clearable
                        outlined
                        :readonly="readOnly"
                        :label="$t('book/issue:add-issue-form.contact-phone')"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <h4 class="primary--text font-weight-medium">
                      {{ $t('book/issue:add-issue-form.contract-data') }}
                    </h4>
                    <v-text-field
                        :label="$t('book/issue:add-issue-form.contract-type')"
                        disabled
                        :readonly="readOnly"
                        :value="contractInfo.contractList"
                    />
                    <v-text-field
                        :label="$t('book/issue:add-issue-form.contract-validity')"
                        disabled
                        :readonly="readOnly"
                        :value="contractInfo.contractEnd"
                    />
                  </v-col>
                </v-row>
                <v-row justify="space-around">
                  <v-col cols="12" md="6">
                    <DateField
                        ref="incidentDate"
                        @update="(v) => task.incidentDate = v"
                        :isClearable="true"
                        :key="incidentDateKey"
                        :readonly="readOnly"
                        :label="$t('book/issue:add-issue-form.task-date')"
                        tabindex="82"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <DateField
                        ref="appointmentDate"
                        @update="(v) => task.appointmentDate = v"
                        :isClearable="true"
                        :key="appointmentDateKey"
                        :readonly="readOnly"
                        :label="$t('book/issue:common.appointment-date')"
                        tabindex="82"
                    />
                  </v-col>
                </v-row>
                <v-row justify="start">
                  <v-col cols="12" md="4">
                    <v-autocomplete
                        :items="userList"
                        :disabled="!userList || userList.length === 0"
                        :readonly="readOnly"
                        clearable
                        outlined
                        item-text="name"
                        item-value="id"
                        v-model="task.manager"
                        :label="$t('book/issue:add-issue-form.assigned-manager')"
                    />
                  </v-col>
                </v-row>
                <v-row justify="start">
                  <v-col cols="12" md="4">
                    <v-text-field
                        v-model="task.externalId"
                        clearable
                        outlined
                        :readonly="readOnly"
                        :label="$t('book/issue:add-issue-form.process-id')"
                    />
                  </v-col>
                </v-row>
                <v-row justify="start">
                  <v-col cols="12" md="4">
                    <v-autocomplete
                        :items="categoryList"
                        v-model="task.category"
                        clearable
                        outlined
                        item-text="translatedName"
                        item-value="id"
                        :readonly="readOnly"
                        :label="$t('book/issue:add-issue-form.categories')"
                        :rules="[ruleRequired($t('utils:rules.required'))]"
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-form>
      </v-col>
    </v-row>
    <v-row justify="end" v-if="!readOnly">
      <v-col>
        <TwoButtonDialog
            ref="saveButton"
            :btn-text="$t('buttons:save')"
            color="primary"
            :hideOverlay="true"
            :dialog-title="$t('dialog:save-btn-title', {name : $t('book/issue:add-issue-form.task')})"
            :dialog-text="$t('dialog:save-btn-dialog', {name : $t('book/issue:add-issue-form.task')})"
            :button1-text="$t('buttons:save')"
            :button2-text="$t('buttons:cancel')"
            @clickedButton1="updateForm()"
            @clickedButton2="$refs.saveButton.showDialog = false"
        />
        <DirtyConfirmButtonDialog
            ref="dirtyDialog"
            :btn-text="$t('buttons:back')"
            color="grey"
            :hideOverlay="true"
            :dialog-title="$t('dialog:dirty-save-btn-title')"
            :dialog-text="$t('dialog:dirty-save-btn-dialog')"
            :button1-text="$t('dialog:dirty-save-btn-1-text')"
            :button2-text="$t('dialog:dirty-save-btn-2-text')"
            @clickedButton="$emit('closedDialog')"
        />
      </v-col>
    </v-row>
  </v-container>
</template>
<script>

import DateField from '@/modules/core/components/DateField.vue';
import DirtyConfirmButtonDialog from '@/modules/core/components/TwoButtonDialog/DirtyConfirmButtonDialog.vue';
import TwoButtonDialog from '@/modules/core/components/TwoButtonDialog/TwoButtonDialog.vue';
import Task from '@/modules/book/data/task';
import {ruleRequired} from '@/modules/core/services/rules';
import TaskBoardService from '@/modules/book/services/task-board-service';
import Utils from '@/modules/core/services/utils';
import DateUtils from '@/modules/core/services/date-utils';
import UsersService from '@/modules/core/services/users-service';
import FacilityService from '@/modules/book/services/facility-service';
import EnumUtils from '@/modules/core/services/enum-utils';
import ContractInfoService from '@/modules/book/services/contract-info-service';
import EnumService from '@/modules/core/services/enum-service';
import InfoService from "@/modules/core/services/info-service";

export default {
  name: 'TaskForm',
  props: {
    readOnly: Boolean,
    facilityUnchangeable: Boolean
  },
  data() {
    return {
      task: new Task(),
      selectedFacility: null,
      facilities: [],
      userList: [],
      categoryList: [],
      contractInfo: {},
      appointmentDateKey: 1,
      incidentDateKey: 1,
      customers: [],
      currentCompany: {}
    }
  },
  components: {TwoButtonDialog, DateField, DirtyConfirmButtonDialog},
  watch: {
    selectedFacility() {
      this.task.facility = this.selectedFacility;
      if (!this.selectedFacility) {
        this.userList = [];
        this.contractInfo = {};
        return;
      }
      this.loadUserList(this.selectedFacility);
      this.loadContractInfo(this.selectedFacility);
    },
    currentCompany() {
      this.facilities = [];
      this.loadFacilities();
    }
  },
  computed: {
    isEdit() {
      return !!this.task && !!this.task.id;
    },
    isAdmin() {
      return this.$store.getters["auth/isAdmin"];
    }
  },
  methods: {
    ruleRequired,
    updateForm() {
      if (!this.$refs.form.validate()) {
        document.getElementsByClassName("v-dialog--active")[0].scrollTop = 0;
        this.$refs.saveButton.showDialog = false;
        return;
      }
      this.task.company = this.currentCompany;

      TaskBoardService.createOrEditTask(
          this.task,
          () => {
            let taskText = this.isEdit ? 'snackbar:edit-success' : 'snackbar:add-success';
            Utils.showSuccessSnackbar(this.$t(taskText, {name: this.$t('book/issue:add-issue-form.task')}));
            this.cleanForm();
            this.$emit('callUpdateDropMenu');
            this.$emit('closedDialog');
          },
          () => {
            let taskText = this.isEdit ? 'snackbar:edit-failure' : 'snackbar:add-failure';
            Utils.showFailureSnackbar(this.$t(taskText, {name: this.$t('book/issue:add-issue-form.task')}));
            this.cleanForm();
            this.$emit('closedDialog');
          }
      )
    },
    cleanForm() {
      this.selectedFacility = null;
      this.task = new Task();
      this.appointmentDateKey++;
      this.incidentDateKey--;
      this.currentCompany = this.$store.state.auth.profile.customer;
    },
    setFormData(item) {
      if (item.facility) {
        this.selectedFacility = item.facility;
      }
      if (item.incidentDate) {
        DateUtils.parseStringToDate(item.incidentDate);
        item.incidentDate = this.$refs.incidentDate.parseDTO(item.incidentDate);
      }
      if (item.appointmentDate) {
        DateUtils.parseStringToDate(item.appointmentDate);
        item.appointmentDate = this.$refs.appointmentDate.parseDTO(item.appointmentDate);
      }

      this.currentCompany = item && item.company ? item.company : this.$store.state.auth.profile.customer;
      this.task = Object.assign({}, item);
    },
    loadUserList(facility) {
      UsersService.loadUsersByElevator(
          facility,
          (response) => {
            this.userList = response.data;
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:user_plural')}));
          }
      );
    },
    loadFacilities() {
      if (!this.currentCompany) return;

      FacilityService.loadFacilityList(
          this.currentCompany,
          (response) => {
            this.facilities = response.data;
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:facility_plural')}));
          }
      )
    },
    loadTaskCategory() {
      EnumUtils.loadEnum('task/category', 'book/task', 'categoryTask',
          (response) => {
            this.categoryList = response;
          }
      );
    },
    loadContractInfo(facility) {
      ContractInfoService.readContractByFacility(
        facility,
        (response) => {
          this.setContractInfo(response.data);
        }
      )
    },
    setContractInfo(contract) {
      this.contractInfo = {
        contractList: '',
        contractEnd: ''
      }
      if (!contract) return;

      let contractTypes = EnumService.translateAndSortEnum(contract.types, 'enums:contract-types.');
      let contractTypesString = '';

      contractTypes.forEach(type => {
        contractTypesString += type.translatedName + ', ';
      })

      if (contractTypesString.length > 0) {
        contractTypesString = contractTypesString.substring(0, contractTypesString.length - 2);
      }

      this.contractInfo = {
        contractList: contractTypesString,
        contractEnd: contract.contractEnd
      }

    },
    loadCustomers() {
      InfoService.readOptionsByUrl('customer/all-customers',
          (customers) => {
            this.customers = customers;
          },
          (error) => {
            this.$logger.error("Error at fetching all customers: " + error);
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:customer_plural')}));
          }
      )
    },
    clearCompany() {
      this.currentCompany = '';
    }
  },
  created() {
    if (this.isAdmin) {
      this.loadCustomers();
    }
    this.currentCompany = this.currentCompany = this.$store.state.auth.profile.customer;
    this.loadTaskCategory();
  }
}
</script>